import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { 
  Bookmark, 
  Clock
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import CareerBookmarkCard from "../components/careerBookmarkCard/CareerBookmarkCard";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";
import { toast } from "react-toastify";

const MySavedCareers = () => {
  const [savedCareers, setSavedCareers] = useState([]);
  const [filteredCareers, setFilteredCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    emerging: 0,
    mainstream: 0,
    lastUpdated: null
  });
  
  const userId = useSelector((state) => state.auth.id);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      fetchSavedCareers();
    }
  }, [userId]);

  useEffect(() => {
    fetchBookmarkStats();
  }, [savedCareers]);

  const fetchSavedCareers = async () => {
    try {
      setLoading(true);
      const response = await api1.get(`${API_ENDPOINTS.GET_CAREER_BOOKMARKS}/${userId}`);
      const careers = response.data.data || [];
      setSavedCareers(careers);
      setFilteredCareers(careers);
    } catch (error) {
      console.error("Error fetching saved careers:", error);
      setSavedCareers([]);
      setFilteredCareers([]);
      toast.error("Failed to load saved careers");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarkStats = async () => {
    try {
      const total = savedCareers.length;
      const emerging = savedCareers.filter(c => c.category === "emerging" || c.careerType === "emerging").length;
      const mainstream = savedCareers.filter(c => c.category === "mainstream" || c.careerType === "mainstream").length;
      
      setStats({
        total,
        emerging,
        mainstream,
        lastUpdated: savedCareers.length > 0 ? savedCareers[0].createdAt : null
      });
    } catch (error) {
      console.error("Error calculating bookmark stats:", error);
      setStats({
        total: 0,
        emerging: 0,
        mainstream: 0,
        lastUpdated: null
      });
    }
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    if (filter === "all") {
      setFilteredCareers(savedCareers);
    } else if (filter === "recently-saved") {
      const sorted = [...savedCareers].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setFilteredCareers(sorted);
    } else {
      const filtered = savedCareers.filter(career => career.category === filter);
      setFilteredCareers(filtered);
    }
  };

  const handleRemoveBookmark = (careerId) => {
    // Remove from frontend state immediately
    const updatedCareers = savedCareers.filter(c => c._id !== careerId);
    setSavedCareers(updatedCareers);
    setFilteredCareers(updatedCareers);
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-purple-500"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="mb-5">
          <div className="flex items-start gap-2 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center mt-1 justify-center">
              <Bookmark className="w-7 h-7 text-purple-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">My Saved Careers</h1>
              <p className="text-gray-600 text-sm">
                Review and manage your bookmarked career opportunities
              </p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Bookmark className="w-4 h-4" />
              <span>{stats.total} saved careers</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Last updated today</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange("all")}
                className={`px-2 py-1 rounded-full border border-gray-300 text-xs font-sm transition-colors ${
                  selectedFilter === "all"
                    ? "bg-gray-800 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All Categories
              </button>
              <button
                onClick={() => handleFilterChange("recently-saved")}
                className={`px-2 py-1 rounded-full border border-gray-300 text-xs font-sm transition-colors ${
                  selectedFilter === "recently-saved"
                    ? "bg-gray-800 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Recently Saved
              </button>
            </div>
            <span className="text-sm text-gray-500">
              Showing {filteredCareers.length} careers
            </span>
          </div>
        </div>

        {/* Career Cards */}
        {filteredCareers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {filteredCareers.map((career) => (
              <CareerBookmarkCard
                key={career._id}
                career={career}
                onRemove={handleRemoveBookmark}
                showRemoveButton={true}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No saved careers found</h3>
            <p className="text-gray-500 mb-6">
              Start exploring careers and save the ones that interest you.
            </p>
            <button
              onClick={() => navigate("/dashboard/career-discovery/career-recommendations")}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Explore Career Recommendations
            </button>
          </div>
        )}

        {/* Explore More Careers Section */}
        {filteredCareers.length > 0 && (
          <div className="mt-5 border border-gray-400 p-5 rounded-lg">
            <h2 className="text-xl font-bold text-gray-9My Saved Careers00 mb-3">Explore More Careers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() => navigate("/dashboard/ai-careers")}
                className="bg-white rounded-lg shadow-md border border-gray-400 p-4 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-bold text-gray-900">Emerging AI Careers</h3>
                <p className="text-sm text-gray-500">Discover cutting-edge roles</p>
              </div>
              <div
                onClick={() => navigate("/dashboard/mainstream-careers")}
                className="bg-white rounded-lg shadow-md border border-gray-400 p-4 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-bold text-gray-900">Mainstream Careers</h3>
                <p className="text-sm text-gray-500">AI-enhanced traditional roles</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default MySavedCareers; 